/**
 * TerrainMacroMicro — macro variation and micro detail for PBR terrain materials.
 *
 * Provides two flavours of each function:
 *   - CPU mirrors (pure TypeScript) for logic testing and tooling
 *   - TSL nodes (GPU) for use inside shader material graphs
 *
 * Macro variation: a low-frequency multiplier (~1.0) applied to surface colour /
 * albedo blending weights to break repeating tiling across large terrain spans.
 *
 * Micro detail: a distance-faded high-frequency normal offset that adds close-up
 * surface roughness without contributing noise at camera distances where it would
 * alias or be imperceptible.
 */

// ─── CPU Mirrors ──────────────────────────────────────────────────────────────

import { fbm } from './TerrainNoiseUtils.js';

/**
 * Macro variation multiplier at a world position.
 *
 * Samples a 3-octave fBm at very low frequency (scale 0.005) so the field
 * changes slowly across the terrain. The result is centred at 1.0 and scaled
 * by `amplitude`, giving a value in roughly [1 - amplitude, 1 + amplitude].
 *
 * @param worldX    - World-space X coordinate
 * @param worldZ    - World-space Z coordinate
 * @param seed      - Integer seed (internally offset by 500 to avoid overlap
 *                    with other noise layers)
 * @param amplitude - Half-range of the multiplier (e.g. 0.1 → [0.9, 1.1])
 * @returns float near 1.0
 */
export function macroVariation(
  worldX: number,
  worldZ: number,
  seed: number,
  amplitude: number,
): number {
  const noise = fbm(worldX * 0.005, worldZ * 0.005, seed + 500, 3);
  return 1.0 + noise * amplitude;
}

/**
 * Micro detail strength based on camera distance.
 *
 * Returns 1.0 when `distance <= fadeStart`, 0.0 when `distance >= fadeEnd`,
 * and a smooth (Hermite) interpolation in between. Using a smoothstep ensures
 * no visible pop at the transition boundary.
 *
 * @param distance  - Distance from camera to surface fragment (metres)
 * @param fadeStart - Distance at which fade begins (full strength below this)
 * @param fadeEnd   - Distance at which fade ends (zero strength above this)
 * @returns float in [0, 1]
 */
export function microDetailStrength(
  distance: number,
  fadeStart: number,
  fadeEnd: number,
): number {
  if (distance <= fadeStart) return 1.0;
  if (distance >= fadeEnd)   return 0.0;
  const t = (distance - fadeStart) / (fadeEnd - fadeStart);
  return 1.0 - t * t * (3 - 2 * t); // smoothstep fade
}

// ─── TSL Nodes ────────────────────────────────────────────────────────────────

import {
  Fn,
  float,
  vec2,
  vec3,
  positionWorld,
  cameraPosition,
  smoothstep,
} from 'three/tsl';
import { tslFbm, tslValueNoiseD } from './TerrainTSLNoise.js';

/**
 * tslMacroVariation — GPU macro variation multiplier (TSL).
 *
 * Mirrors `macroVariation()` in shader space. Samples world XZ at scale 0.005
 * through a 3-octave fBm and returns a float centred at 1.0.
 *
 * @param seed      - float seed (internally offset by 500.0)
 * @param amplitude - float half-range of multiplier
 * @returns float near 1.0
 */
 
export const tslMacroVariation = Fn(([seed, amplitude]: any) => {
  const worldXZ = vec2(positionWorld.x, positionWorld.z).mul(float(0.005));
  // tslFbm is typed as () => Node by Fn inference; cast to any for variadic call.
   
  const noise = (tslFbm as any)(worldXZ, float(seed).add(float(500.0)), float(3.0));
  return float(1.0).add(noise.mul(float(amplitude)));
});

/**
 * tslMicroStrength — GPU micro detail strength (TSL).
 *
 * Computes the distance from the current fragment's world position to the
 * camera, then applies a smooth fade from 1.0 (≤ fadeStart) to 0.0 (≥ fadeEnd).
 *
 * Note: `smoothstep(edge0, edge1, x)` returns 0 at edge0 and 1 at edge1, so
 * the result is inverted to give 1 close-up and 0 at distance.
 *
 * @param fadeStart - float distance where fade begins
 * @param fadeEnd   - float distance where fade reaches zero
 * @returns float in [0, 1]
 */
 
export const tslMicroStrength = Fn(([fadeStart, fadeEnd]: any) => {
  const dist = positionWorld.sub(cameraPosition).length();
  return float(1.0).sub(smoothstep(float(fadeStart), float(fadeEnd), dist));
});

/**
 * tslMicroDetailNormal — GPU micro detail tangent-space normal offset (TSL).
 *
 * Evaluates derivative value noise at high frequency (noiseScale * 4.0) to
 * produce a per-fragment normal perturbation. Returns a vec3 in tangent space:
 *   (−dx * strength, −dz * strength, 1.0)
 *
 * The negative sign follows the convention that a positive noise gradient
 * along +X means the surface tilts toward −X, so the normal leans in −X.
 *
 * @param seed       - float seed (internally offset by 700.0)
 * @param strength   - float perturbation magnitude (e.g. 0.1–0.5)
 * @param noiseScale - float base UV scale; multiplied by 4 for micro detail
 * @returns vec3 tangent-space normal (not normalised; caller can normalise)
 */
 
export const tslMicroDetailNormal = Fn(([seed, strength, noiseScale]: any) => {
  const uv = vec2(positionWorld.x, positionWorld.z)
    .mul(float(noiseScale))
    .mul(float(4.0));
  const nd = tslValueNoiseD(uv, float(seed).add(float(700.0)));
  // nd = vec3(value, dx, dz); use .y for dx and .z for dz
  // Return Y-up convention to match terrain normalNode: vec3(X, Y_up, Z)
  return vec3(
    nd.y.mul(float(strength)).negate(),
    float(1.0),
    nd.z.mul(float(strength)).negate(),
  );
});
